import { useEffect, useRef, useState, type FormEvent } from "react";
import { Loading } from "@/components/Loading";
import { Notice } from "@/components/Notice";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { ApiError, type IdResponse, type OperationResult } from "@/lib/apiClient";
import { tooLong } from "@/lib/fieldLimits";
import { type NamedItem } from "@/features/org/api/org.api";

type ListApi = {
  list: () => Promise<NamedItem[]>;
  create: (name: string, description?: string | null) => Promise<IdResponse>;
  update: (id: string, name: string, description?: string | null) => Promise<OperationResult>;
  remove: (id: string) => Promise<void>;
};

export default function NamedListPage({
  api, title, subtitle, noun, nameMax, descMax,
}: {
  api: ListApi;
  title: string;
  subtitle: string;
  noun: string;
  nameMax: number;
  descMax: number;
}) {
  const [rows, setRows] = useState<NamedItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [addTried, setAddTried] = useState(false);
  const [busy, setBusy] = useState(false);

  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editTried, setEditTried] = useState(false);
  const editNameRef = useRef<HTMLInputElement>(null);
  const confirm = useConfirm();

  const newNameEmpty = !newName.trim();
  const newNameTooLong = newName.length > nameMax;
  const newDescTooLong = newDesc.length > descMax;
  const editNameEmpty = !editName.trim();
  const editNameTooLong = editName.length > nameMax;
  const editDescTooLong = editDesc.length > descMax;

  const newNameError = !addTried ? null : newNameTooLong ? tooLong("Name", nameMax) : newNameEmpty ? "Name is required." : null;
  const newDescError = addTried && newDescTooLong ? tooLong("Description", descMax) : null;
  const editNameError = !editTried ? null : editNameTooLong ? tooLong("Name", nameMax) : editNameEmpty ? "Name is required." : null;
  const editDescError = editTried && editDescTooLong ? tooLong("Description", descMax) : null;

  function load() {
    api.list()
      .then(setRows)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 403) setForbidden(true);
        else setError(e instanceof Error ? e.message : "Failed to load.");
      });
  }
  useEffect(load, []);

  useEffect(() => {
    if (editId) {
      editNameRef.current?.focus();
      editNameRef.current?.select();
    }
  }, [editId]);

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    setAddTried(true);
    if (newNameEmpty || newNameTooLong || newDescTooLong) return;
    setBusy(true);
    setError(null);
    try {
      await api.create(newName.trim(), newDesc.trim() || null);
      setNewName(""); setNewDesc(""); setAddTried(false);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't add it.");
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit() {
    if (!editId) return;
    setEditTried(true);
    if (editNameEmpty || editNameTooLong || editDescTooLong) return;
    setBusy(true);
    setError(null);
    try {
      await api.update(editId, editName.trim(), editDesc.trim() || null);
      setEditId(null); setEditTried(false);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't save.");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(item: NamedItem) {
    if (!(await confirm({ description: `Delete ${noun} "${item.name}"?`, confirmLabel: "Delete", destructive: true }))) return;
    setError(null);
    try {
      await api.remove(item.id);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't delete.");
    }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">{title}</h1>
        <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
      </header>

      {forbidden && (
        <Notice tone="warn">You don't have permission to manage {title.toLowerCase()} — this is admin-only.</Notice>
      )}
      {error && !forbidden && <Notice tone="error" className="mb-6">{error}</Notice>}

      {!forbidden && (
        <div className="space-y-6">
          <Card>
            <CardContent className="p-5">
              <h2 className="text-sm font-semibold text-slate-800 mb-3">Add {noun}</h2>
              <form onSubmit={onAdd} className="flex flex-col md:flex-row md:items-start gap-2">
                <div className="md:flex-1">
                  <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name" aria-invalid={!!newNameError} />
                  {newNameError && <p className="mt-1 text-xs text-destructive">{newNameError}</p>}
                </div>
                <div className="md:flex-1">
                  <Input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Description (optional)" aria-invalid={!!newDescError} />
                  {newDescError && <p className="mt-1 text-xs text-destructive">{newDescError}</p>}
                </div>
                <Button type="submit" disabled={busy} className="whitespace-nowrap">Add</Button>
              </form>
            </CardContent>
          </Card>

          <Card className="overflow-hidden">
            {rows === null ? (
              <Loading className="p-5">Loading…</Loading>
            ) : rows.length === 0 ? (
              <p className="text-sm text-muted-foreground p-5">No {title.toLowerCase()} yet.</p>
            ) : (
              <Table>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      {editId === r.id ? (
                        <TableCell colSpan={2}>
                          <div className="flex flex-col md:flex-row md:items-start gap-2">
                            <div className="md:flex-1">
                              <Input ref={editNameRef} value={editName} onChange={(e) => setEditName(e.target.value)} className="h-9" aria-invalid={!!editNameError} />
                              {editNameError && <p className="mt-1 text-xs text-destructive">{editNameError}</p>}
                            </div>
                            <div className="md:flex-1">
                              <Input value={editDesc} onChange={(e) => setEditDesc(e.target.value)} placeholder="Description" className="h-9" aria-invalid={!!editDescError} />
                              {editDescError && <p className="mt-1 text-xs text-destructive">{editDescError}</p>}
                            </div>
                            <Button type="button" size="sm" variant="outline" onClick={saveEdit} disabled={busy}>Save</Button>
                            <Button type="button" size="sm" variant="ghost" onClick={() => { setEditId(null); setEditTried(false); }}>Cancel</Button>
                          </div>
                        </TableCell>
                      ) : (
                        <>
                          <TableCell>
                            <div className="font-medium text-slate-800">{r.name}</div>
                            {r.description && <div className="text-xs text-slate-500">{r.description}</div>}
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <Button
                              type="button"
                              size="sm"
                              variant="link"
                              className="h-auto p-0 mr-3"
                              onClick={() => { setEditId(r.id); setEditName(r.name); setEditDesc(r.description ?? ""); setEditTried(false); }}
                            >
                              Edit
                            </Button>
                            <Button type="button" size="sm" variant="link" className="h-auto p-0 text-destructive" onClick={() => onDelete(r)}>
                              Delete
                            </Button>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
